import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMemberAuth } from '../contexts/MemberAuthContext';
import { getOrgConfig } from '../api/orgApi';
import '../styles/member.css';

// Tool components
import GovSchemesMember          from '../components/member/programmes/GovSchemesMember';
import OrgProgrammesMember       from '../components/member/programmes/OrgProgrammesMember';
import JobBoardMember            from '../components/member/programmes/JobBoardMember';
import SupportNetworkMember      from '../components/member/programmes/SupportNetworkMember';
import EventsDiscoverMember      from '../components/member/events/EventsDiscoverMember';
import MyEventsMember            from '../components/member/events/MyEventsMember';
import EventResourcesMember      from '../components/member/events/EventResourcesMember';
import MyProfileMember           from '../components/member/community/MyProfileMember';
import MemberHome                from '../components/member/home/MemberHome';
import NotificationBell          from '../components/shared/NotificationBell';

const NAV_ICONS = {
  home: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
      <path d="M9 21V12h6v9"/>
    </svg>
  ),
  schemes: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/>
    </svg>
  ),
  programmes: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
    </svg>
  ),
  jobs: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2M12 12v4M10 14h4"/>
    </svg>
  ),
  support: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  events: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  profile: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
};

const ALL_MEMBER_TOOLS = [
  { key: 'home',       label: 'Home',                    icon: NAV_ICONS.home,       moduleKey: null         },
  { key: 'schemes',    label: 'Government Programmes',   icon: NAV_ICONS.schemes,    moduleKey: 'programmes' },
  { key: 'programmes', label: 'Organisation Programmes', icon: NAV_ICONS.programmes, moduleKey: 'programmes' },
  { key: 'jobs',       label: 'Job Board',               icon: NAV_ICONS.jobs,       moduleKey: 'supportmgmt' },
  { key: 'support',    label: 'Support',                 icon: NAV_ICONS.support,    moduleKey: 'supportmgmt' },
  { key: 'events',     label: 'Events',                  icon: NAV_ICONS.events,     moduleKey: 'events'     },
  { key: 'profile',    label: 'My Profile',              icon: NAV_ICONS.profile,    moduleKey: null         },
];

const TOOL_TABS = {
  home:       ['Appointments', 'Events', 'Office Hours'],
  schemes:    ['Explore Schemes', 'Eligibility Check', 'My Referrals'],
  programmes: ['Organisation Programmes', 'Eligibility Checker', 'My Referrals'],
  jobs:       ['Browse Jobs', 'My Applications'],
  support:    ['Support Options', 'My Connections'],
  events:     ['Discover Events', 'My Registrations', 'Event Resources'],
  profile:    ['My Details', 'Privacy Settings', 'Subscription Preferences'],
};

function MemberPortal({ toolHint = 'schemes' }) {
  const { orgId } = useParams();
  const { currentMember, logout } = useMemberAuth();
  const navigate = useNavigate();

  const [activeTool, setActiveTool] = useState(toolHint);
  const [activeTab, setActiveTab] = useState(TOOL_TABS[toolHint]?.[0] || 'Explore Schemes');
  const [homeScrollSignal, setHomeScrollSignal] = useState(0);
  const [orgConfig, setOrgConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Sync tool from prop (URL-driven)
  useEffect(() => {
    setActiveTool(toolHint);
    setActiveTab(TOOL_TABS[toolHint]?.[0] || '');
  }, [toolHint]);

  useEffect(() => {
    const load = async () => {
      try {
        const config = await getOrgConfig(orgId);
        setOrgConfig(config);
      } catch (err) {
        console.error('Error loading org config:', err);
      } finally {
        setLoading(false);
      }
    };
    if (orgId) load();
  }, [orgId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToolNav = (toolKey) => {
    setActiveTool(toolKey);
    setActiveTab(TOOL_TABS[toolKey]?.[0] || '');
    navigate(`/BespokeBridge/${orgId}/member/${toolKey}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate(`/BespokeBridge/${orgId}/member/login`);
  };

  if (loading) {
    return (
      <div className="bb-member-loading-screen">
        <div className="bb-loading-spinner"></div>
        <p>Loading member portal...</p>
      </div>
    );
  }

  const brandColors = orgConfig?.branding?.colors || {};
  const orgCssVars = {
    '--org-primary':    brandColors.primary    || '#3A7D44',
    '--org-secondary':  brandColors.secondary  || '#B8D9A0',
    '--org-accent':     brandColors.accent     || '#EBF5E4',
    '--org-background': brandColors.background || '#F5F0E8',
    '--org-text':       brandColors.text       || '#2D3A2E',
  };

  const enabledModules = orgConfig?.enabledModules;
  const MEMBER_TOOLS = ALL_MEMBER_TOOLS.filter(t => {
    if (!t.moduleKey) return true;
    if (!enabledModules) return true;
    return !!enabledModules[t.moduleKey];
  });

  const memberName = currentMember?.displayName || currentMember?.name || currentMember?.email || 'Member';
  const avatarLetter = memberName.charAt(0).toUpperCase();

  const sharedProps = {
    orgId,
    memberId: currentMember?.uid,
    memberName: currentMember?.displayName || currentMember?.name || currentMember?.email,
    memberEmail: currentMember?.email,
    activeTab,
    setActiveTab,
  };

  return (
    <div className="bb-member" style={orgCssVars}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="bb-member-header">
        <div className="bb-member-header-brand">
          <div className="bb-brand-row">
            <span className="bb-brand-name">BespokeBridge</span>
            <span className="bb-portal-type">Member Portal</span>
            <span className="bb-org-id-badge">{orgId}</span>
          </div>
        </div>

        {/* User area */}
        <div className="bb-member-header-user" ref={userMenuRef} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NotificationBell orgId={orgId} recipientId={currentMember?.uid} accentColor={brandColors.primary || '#3A7D44'} />
          <button
            className="bb-member-user-menu"
            onClick={() => setUserMenuOpen(v => !v)}
          >
            <div className="bb-member-user-avatar">{avatarLetter}</div>
            <span className="bb-member-user-name">{memberName}</span>
            <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
          </button>
          {userMenuOpen && (
            <div className="bb-member-user-dropdown">
              <div className="bb-member-dropdown-info">
                <div className="bb-member-dropdown-name">{memberName}</div>
                <div className="bb-member-dropdown-email">{currentMember?.email}</div>
              </div>
              <hr className="bb-member-dropdown-divider" />
              <button
                className="bb-member-dropdown-item"
                onClick={() => { handleToolNav('profile'); setUserMenuOpen(false); }}
              >
                👤 My Profile
              </button>
              <button
                className="bb-member-dropdown-item bb-member-dropdown-logout"
                onClick={handleLogout}
              >
                ⎋ Sign Out
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ── Sub-header: tools (row 1) + tab pills (row 2) ───────────────── */}
      <div className="bb-tab-bar">
        <div className="bb-tab-bar-groups">
          {MEMBER_TOOLS.map((tool) => (
            <button
              key={tool.key}
              className={`bb-tab-group-btn${activeTool === tool.key ? ' selected' : ''}`}
              onClick={() => handleToolNav(tool.key)}
            >
              <span className="bb-tab-group-icon">{tool.icon}</span>
              <span className="bb-tab-group-label">{tool.label}</span>
            </button>
          ))}
        </div>
        <div className="bb-tab-bar-items">
          {(TOOL_TABS[activeTool] || []).map((tab) => (
            <button
              key={tab}
              className={`bb-tab-item-btn${activeTab === tab ? ' selected' : ''}`}
              onClick={() => {
                setActiveTab(tab);
                if (activeTool === 'home') setHomeScrollSignal(s => s + 1);
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Main content */}
      <main className="bb-member-main">
          {/* HOME — all sections on one scrollable page */}
          {activeTool === 'home' && (
            <MemberHome
              orgId={orgId}
              memberId={currentMember?.uid}
              memberName={currentMember?.displayName || currentMember?.name || currentMember?.email}
              memberEmail={currentMember?.email}
              officeHours={orgConfig?.officeHours}
              orgName={orgConfig?.branding?.name || orgConfig?.aboutUs?.name || orgId}
              orgConfig={orgConfig}
              activeTab={activeTab}
              scrollSignal={homeScrollSignal}
            />
          )}

          {activeTool === 'schemes' && (
            <GovSchemesMember {...sharedProps} />
          )}
          {activeTool === 'programmes' && (
            <OrgProgrammesMember {...sharedProps} />
          )}
          {activeTool === 'jobs' && (
            <JobBoardMember
              {...sharedProps}
              memberEmail={currentMember?.email}
            />
          )}
          {activeTool === 'support' && (
            <SupportNetworkMember {...sharedProps} />
          )}
          {activeTool === 'events' && activeTab === 'Discover Events'   && <EventsDiscoverMember {...sharedProps} />}
          {activeTool === 'events' && activeTab === 'My Registrations'  && <MyEventsMember {...sharedProps} />}
          {activeTool === 'events' && activeTab === 'Event Resources'   && <EventResourcesMember {...sharedProps} />}
          {activeTool === 'profile'    && activeTab === 'My Details'              && <MyProfileMember {...sharedProps} />}
          {activeTool === 'profile'    && activeTab === 'Privacy Settings'        && <MyProfileMember {...sharedProps} />}
          {activeTool === 'profile'    && activeTab === 'Subscription Preferences' && <MyProfileMember {...sharedProps} />}
      </main>
    </div>
  );
}

export default MemberPortal;
